import { type Profile, Role } from '@gm-commercial/profile-model';
import { matchPath } from 'react-router-dom';
import { ADMIN_ROLES } from '~/common/constants/common.constant';
import { AppNames } from '~/common/models/appNames.model';
import {
  AppSpaceCode,
  type TrackableAppSpaceCode,
} from '~/features/welcomeExperience/gettingStarted.model';
import {
  getActingOrgId,
  hasPersistedActingOrgContext,
} from '~/store/profile/actingProfile.utils';
import { type RouteParams, SecureRoutes } from '../../configs/route.config';
import type { FeatureFlagValueType } from '../../models/featureFlags.model';
import {
  AppRoutePaths,
  type RouteConfig,
  RouteName,
} from '../../models/route.model';

export const findRoute = (routeName: RouteName): RouteConfig =>
  SecureRoutes.find((route) => route.name === routeName) || ({} as RouteConfig);

export const findRouteFromPath = (
  path: string | undefined
): RouteConfig | undefined => {
  if (!SecureRoutes || !path) {
    return undefined;
  }
  return SecureRoutes.find((route: RouteConfig) =>
    new RegExp(`^${path}$`).test(route.path)
  );
};

type MakeRoutePathParams = {
  pathInfo: string | RouteConfig;
  params?: RouteParams;
  exact?: boolean;
  includeQuery?: boolean;
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
};
const defaultRouteParams: RouteParams = {};

// Overload signatures to simplify usage
export function makeRoutePath(params: MakeRoutePathParams): string;
export function makeRoutePath(
  pathInfo: string | RouteConfig,
  params: RouteParams,
  exact?: boolean,
  includeQuery?: boolean,
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined
): string;
// Implementation
export function makeRoutePath(
  pathInfoOrParams: string | RouteConfig | MakeRoutePathParams,
  params = defaultRouteParams,
  exact = false,
  includeQuery = false,
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined
): string {
  // Handle different call signatures
  let pathInfo: string | RouteConfig = '';
  if (typeof pathInfoOrParams === 'object' && 'pathInfo' in pathInfoOrParams) {
    pathInfo = pathInfoOrParams.pathInfo;
    params = pathInfoOrParams.params || defaultRouteParams;
    exact = pathInfoOrParams.exact ?? exact;
    includeQuery = pathInfoOrParams.includeQuery ?? includeQuery;
    findFlagScope = pathInfoOrParams.findFlagScope;
  } else {
    pathInfo = pathInfoOrParams;
  }

  // candidate paths either
  //   a) end with the string-type pathInfo, or
  //   b) are all the paths of the provided RouteConfig
  // if exact === true, pathInfo is assumed to be the desired path
  let candidatePaths: string[] = [];
  if (exact && pathInfo && typeof pathInfo === 'string') {
    candidatePaths.push(pathInfo);
  } else {
    if (typeof pathInfo === 'string') {
      candidatePaths = SecureRoutes.reduce((flattened, config) => {
        if (config?.path) {
          if (typeof config.path === 'string') {
            flattened.push(config.path);
          } else {
            flattened = flattened.concat(config.path);
          }
        }
        return flattened;
      }, [] as string[]).filter((path) => path.endsWith(`/${pathInfo}`));
    } else if (pathInfo.path) {
      /**
       * If this is true we know we are dealing with a RouteConfig and will check for feature flags on the route.
       * If the feature is disabled we will return an empty path.
       */
      const requiredFlagsDisabled =
        !!findFlagScope &&
        !!pathInfo?.requiredFeatureFlags?.length &&
        pathInfo.requiredFeatureFlags.some(
          (flagName) => !findFlagScope(flagName)
        );
      // anyFeatureFlags is OR-only and additive: only gates the path when populated.
      const anyFlagsDisabled =
        !!findFlagScope &&
        !!pathInfo?.anyFeatureFlags?.length &&
        !pathInfo.anyFeatureFlags.some((flagName) => !!findFlagScope(flagName));

      if (requiredFlagsDisabled || anyFlagsDisabled) {
        return '';
      }
      if (typeof pathInfo.path === 'string') {
        candidatePaths.push(pathInfo.path);
      } else {
        candidatePaths = candidatePaths.concat(pathInfo.path || []);
      }
    }
  }

  if (!candidatePaths.length) {
    return '';
  }

  // set fallback path to any that doesn't require params if no params are available
  let mostQualifiedPath =
    candidatePaths.find((p) => p.indexOf(':') === -1) || '';
  const usedParams: string[] = [];
  candidatePaths.reduce((mostParams, path) => {
    let workingPath = path;
    let paramsSupported = 0;

    for (const param in params) {
      const paramValue = params[param as keyof RouteParams];
      if (
        Object.hasOwn(params, param) &&
        paramValue &&
        path.indexOf(`:${param}`) > 0
      ) {
        workingPath = workingPath.replace(`:${param}`, paramValue);
        paramsSupported++;
        usedParams.push(param);
      }
    }

    // select working path if it makes the most use of available route params and
    // does not require any params that are not currently available
    if (paramsSupported > mostParams && workingPath.indexOf(':') === -1) {
      mostQualifiedPath = workingPath;
      return paramsSupported;
    }
    return mostParams;
  }, 0);

  if (includeQuery) {
    const availableParams = Object.keys(params)
      .filter(
        (param) =>
          !usedParams.includes(param) && params[param as keyof RouteParams]
      )
      .reduce(
        (accumulator, param) => {
          accumulator[param] = params[param as keyof RouteParams] ?? '';
          return accumulator;
        },
        {} as Record<string, string>
      );
    const queryValue = new URLSearchParams({ ...availableParams }).toString();
    return queryValue
      ? `${mostQualifiedPath}?${queryValue}`
      : mostQualifiedPath;
  }

  return mostQualifiedPath;
}

export const getDefaultRoutePath = (
  role: Role | undefined,
  params: RouteParams,
  profile?: Profile
): string => {
  let route: string;
  switch (role) {
    case Role.ORG_MANAGER:
    case Role.FLEET_MANAGER:
      if (profile?.userPreferences?.showGettingStarted) {
        route = makeRoutePathLite({
          routeName: RouteName.GETTING_STARTED,
          params,
        });
      } else {
        route = makeRoutePathLite({
          routeName: RouteName.ORGANIZATIONS_HOME,
          params,
        });
      }
      break;
    case Role.OPERATOR:
      route = AppRoutePaths.MOBILE_APP_REQUIRED;
      break;
    case Role.SYSTEM_MANAGER:
    case Role.SYSTEM_READER:
    case Role.GMF_ADVISOR:
      route = '/organizations';
      break;
    case Role.TECHNICAL_MANAGER:
      route = AppRoutePaths.CS_BACKOFFICE;
      break;
    default:
      route = AppRoutePaths.UNAUTHORIZED;
      break;
  }
  return route;
};

/**
 * Resolves the destination after a user successfully accepts terms.
 * Migrated customers go to the OnStar platform ready screen; others use the
 * role-based default route.
 * @param profile - Signed-in user profile
 * @param params - Route params used to resolve org/fleet path segments
 * @returns Route path to navigate to after terms acceptance
 */
export const getPostTermsAcceptanceRoute = (
  profile: Profile | undefined,
  params: RouteParams
): string => {
  if (profile?.migrated === true) {
    return findRoute(RouteName.ONSTAR_PLATFORM_IS_READY).path;
  }

  return getDefaultRoutePath(profile?.role, params, profile);
};

/**
 * The impersonated organization's home page, or undefined when the user is not
 * impersonating.
 *
 * Impersonation is only ended by logout or "Exit organization", so every
 * advisor-scoped destination resolves here instead. Route guards authorize with
 * the acting role, which would deny the advisor's own landing pages.
 *
 * @param profile - The signed-in user's base profile.
 * @param actingProfile - The profile the user is currently acting as.
 */
export const getImpersonationHomePath = (
  profile: Profile | undefined,
  actingProfile: Profile | undefined
): string | undefined => {
  if (profile?.role === Role.GMF_ADVISOR) return undefined;
  if (!hasPersistedActingOrgContext(profile, actingProfile)) {
    return undefined;
  }

  const organizationsId = getActingOrgId(actingProfile);
  return organizationsId
    ? makeRoutePathLite({
        routeName: RouteName.ORGANIZATIONS_HOME,
        params: { organizationsId },
      })
    : undefined;
};

/**
 * Advisor org-list path, or undefined when the user is impersonating or is not
 * an advisor. "Exit organization" clears the acting org while still on the
 * current page, so a route the advisor cannot see must fall back here instead
 * of the denial screen. Matches the banner's destination.
 *
 * @param profile - The signed-in user's base profile.
 * @param actingProfile - The profile the user is currently acting as.
 */
export const getAdvisorHomePath = (
  profile: Profile | undefined,
  actingProfile: Profile | undefined
): string | undefined => {
  if (profile?.role === Role.GMF_ADVISOR) return '/organizations';
  if (hasPersistedActingOrgContext(profile, actingProfile)) {
    return undefined;
  }

  if (!profile?.role || !ADMIN_ROLES.includes(profile.role)) {
    return undefined;
  }

  return findRoute(RouteName.ORGANIZATIONS).path;
};

export const getNavDisabledPaths = (): string[] => [
  findRoute(RouteName.USER_TERMS_AND_CONDITIONS).path,
  findRoute(RouteName.ORGANIZATION_TERMS_AND_CONDITIONS).path,
  findRoute(RouteName.ONSTAR_PLATFORM_IS_READY).path,
  findRoute(RouteName.MIGRATION_NOT_COMPLETE).path,
  findRoute(RouteName.TERMS_NOT_SIGNED).path,
  AppRoutePaths.ROOT,
  `${AppRoutePaths.BUSINESS_ENROLLMENT}/*`,
  AppRoutePaths.TERMS_CONDITIONS,
  AppRoutePaths.CANCELLED_INVITE,
  AppRoutePaths.EXPIRED_INVITE,
  AppRoutePaths.LOGIN,
  AppRoutePaths.PRIVACY,
  AppRoutePaths.MOBILE_APP_REQUIRED,
  `${AppRoutePaths.QUICK_PAY}/*`,
];

/**
 * Returns routes that should not display the global header.
 *
 * The mobile-app-required route keeps the account header available so
 * authenticated operators can sign out while the navigation drawer stays hidden.
 *
 * @returns Route patterns where the global header is disabled
 */
export const getHeaderDisabledPaths = (): string[] =>
  getNavDisabledPaths().filter(
    (path) => path !== AppRoutePaths.MOBILE_APP_REQUIRED
  );

export const getProfileDisabled = (): string[] => [
  findRoute(RouteName.USER_TERMS_AND_CONDITIONS).path,
  findRoute(RouteName.ONSTAR_PLATFORM_IS_READY).path,
];

export const isImmersiveNavPath = (path: string): boolean => {
  return [AppNames.ACCOUNT, AppNames.SETTINGS, AppNames.SUPPORT].some(
    (appName) => path.startsWith(`/${appName}/`)
  );
};

const applyPathParams = (path: string, params: RouteParams): string => {
  let newPath = path;

  for (const param in params) {
    const paramRegex = new RegExp(`:${param}`);
    if (paramRegex.test(newPath)) {
      newPath = newPath.replace(
        `:${param}`,
        params[param as keyof RouteParams] ?? ''
      );
    }
  }

  return newPath;
};

type MakeRoutePathLiteParams = {
  routeName: RouteName;
  params: RouteParams;
  search?: string;
  findFlagScope?: (flagName: string) => FeatureFlagValueType | undefined;
};

export const makeRoutePathLite = ({
  routeName,
  params,
  search = '',
  findFlagScope,
}: MakeRoutePathLiteParams): string => {
  const route = findRoute(routeName);
  let routePath = route.path as string;
  if (Array.isArray(route.path)) {
    routePath = route.path?.[0] || '';
  }
  const path = applyPathParams(routePath, params);

  if (findFlagScope) {
    const requiredFlagsDisabled =
      !!route.requiredFeatureFlags?.length &&
      route.requiredFeatureFlags.some((name) => !findFlagScope(name));
    // anyFeatureFlags is OR-only and additive: only gates the path when populated.
    const anyFlagsDisabled =
      !!route.anyFeatureFlags?.length &&
      !route.anyFeatureFlags.some((name) => !!findFlagScope(name));
    if (requiredFlagsDisabled || anyFlagsDisabled) {
      return '';
    }
  }

  if (search.length) {
    const query = new URLSearchParams(search);
    return `${path}?${query.toString()}`;
  }

  return path;
};

export const findMatchingRouteFromPathname = (
  pathname: string
): RouteConfig | undefined => {
  return SecureRoutes.find((route) => {
    const paths = Array.isArray(route.path) ? route.path : [route.path];
    return paths.some((path) => matchPath(path, pathname) !== null);
  });
};

const APP_SPACE_ROUTE_NAMES: Record<AppSpaceCode, RouteName> = {
  [AppSpaceCode.BUSINESS_PROFILE]: RouteName.CS_PLATFORM_ACCOUNT_ORG_DETAILS,
  [AppSpaceCode.VEHICLES]: RouteName.ORGANIZATIONS_VEHICLES,
  [AppSpaceCode.DRIVERS]: RouteName.CS_PLATFORM_DRIVERS_LIST,
  [AppSpaceCode.USERS]: RouteName.CS_PLATFORM_USER_LIST,
  [AppSpaceCode.GEOFENCES]: RouteName.ORGANIZATIONS_MAP_GEOFENCES,
  [AppSpaceCode.ORDERS]: RouteName.CS_PLATFORM_ORDER_LIST,
  [AppSpaceCode.DATA_INSIGHTS]: RouteName.ORGANIZATIONS_REPORTS,
  [AppSpaceCode.DRIVER_SCORES]: RouteName.ORGANIZATIONS_SAFETY_LEADERBOARD,
  [AppSpaceCode.ONSTAR_PLANS]: RouteName.SHOP_PLANS,
  [AppSpaceCode.DEVICES]: RouteName.CS_PLATFORM_PERIPHERALS_LIST,
};

const ROUTES_TO_TRACKABLE_APP_SPACE_CODE: Partial<
  Record<RouteName, TrackableAppSpaceCode>
> = {
  [RouteName.CS_PLATFORM_ACCOUNT_ORG_DETAILS]: AppSpaceCode.BUSINESS_PROFILE,
  [RouteName.ORGANIZATIONS_VEHICLES]: AppSpaceCode.VEHICLES,
  [RouteName.CS_PLATFORM_DRIVERS_LIST]: AppSpaceCode.DRIVERS,
  [RouteName.CS_PLATFORM_USER_LIST]: AppSpaceCode.USERS,
  [RouteName.ORGANIZATIONS_MAP_VEHICLE_DETAILS_OVERVIEW]:
    AppSpaceCode.GEOFENCES,
};

/**
 * Builds a route path for a welcome-experience app space.
 * @param spaceCode - App space to navigate to
 * @param params - Route params used to resolve org/fleet path segments
 * @returns Resolved route path for the given app space
 */
export const getAppSpaceRoute = (
  spaceCode: AppSpaceCode,
  params: RouteParams
): string =>
  makeRoutePathLite({
    routeName: APP_SPACE_ROUTE_NAMES[spaceCode],
    params,
  });

/**
 * Invokes the viewed-app-space handler when the current route maps to a
 * trackable welcome-experience app space.
 * @param routeName - Current matched route name
 * @param handleSetAppSpaceViewed - Callback that records the space as viewed
 */
export const setAppSpaceViewed = (
  routeName: RouteName,
  handleSetAppSpaceViewed: (spaceCode: TrackableAppSpaceCode) => unknown
): void => {
  const spaceCode = ROUTES_TO_TRACKABLE_APP_SPACE_CODE[routeName];
  if (!spaceCode) {
    return;
  }

  handleSetAppSpaceViewed(spaceCode);
};
